import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * Retry-payout confirmation dialog (AUT-128). The retry endpoint takes an
 * EMPTY body (the action itself is the forensic signal), so this dialog
 * collects no input — it just confirms the destructive "cancel + replace"
 * intent before the parent opens the TOTP step-up.
 *
 * Backend remains the 422 authority on the actual retry (rail unsupported
 * / KYC-blocked / payment-blocked) and the 409 on a stale state — this
 * dialog only gates the click.
 */
export interface RetryPayoutDialogProps {
  open: boolean;
  loading?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

export const RetryPayoutDialog = ({
  open,
  loading = false,
  onConfirm,
  onCancel,
}: RetryPayoutDialogProps): ReactElement => {
  const { t } = useTranslation();
  return (
    <Dialog open={open} onClose={loading ? undefined : onCancel} maxWidth="xs" fullWidth>
      <DialogTitle>{t('payouts.retryDialog.title')}</DialogTitle>
      <DialogContent>
        <DialogContentText>{t('payouts.retryDialog.description')}</DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} disabled={loading}>
          {t('common.cancel')}
        </Button>
        <Button
          variant="contained"
          color="primary"
          disabled={loading}
          loading={loading}
          onClick={() => {
            if (!loading) onConfirm();
          }}
        >
          {t('payouts.retryDialog.confirm')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
